import { useFormContext } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, type ComponentProps } from 'react';

import { getResolutionId } from '../../../utils/resolution';
import { type WorkflowData } from '../../../workflow';
import { useWorkflow } from '../../../workflow/hooks';
import { type TaskComponentType } from '../../../workflow/schemaTypes';
import type { ResolutionOption } from '../../../graphql/query/getResolutionOptions';
import { useItemResolutions } from '../../../hooks/useItemResolutions';
import { ResolutionTaskView } from './ResolutionTaskView';

export const ResolutionTask: TaskComponentType = ({ itemId }) => {
  const { getValues, setValue, register, watch, resetField } =
    useFormContext<WorkflowData>();
  const { data, retailer } = useWorkflow();
  const navigate = useNavigate();
  const selectedResolutionId = getResolutionId(data, itemId);
  const { isLoading, resolutionOptions } = useItemResolutions(itemId);
  const selectedResolution = resolutionOptions?.find(
    ({ id }) => id === selectedResolutionId,
  );
  const exchangeItem = watch(`items.${itemId}.resolution.exchangeItem`);

  useEffect(() => {
    const isExchangeResolution = selectedResolution?.exchanges;
    if (
      isExchangeResolution &&
      !exchangeItem?.productId &&
      !exchangeItem?.variantId
    ) {
      resetField(`items.${itemId}.resolution.resolutionId`, {
        defaultValue: undefined,
      });
    }
    if (!isExchangeResolution && exchangeItem) {
      setValue(`items.${itemId}.resolution.exchangeItem`, undefined);
    }
  }, [
    itemId,
    exchangeItem,
    selectedResolution,
    selectedResolutionId,
    resolutionOptions,
    getValues,
    resetField,
    setValue,
  ]);

  // Preselect the resolution option when there is only one choice
  useEffect(() => {
    if (isLoading) return;
    if (!resolutionOptions || resolutionOptions.length !== 1) return;
    // Only preselect if no valid resolution is currently selected
    if (selectedResolutionId && selectedResolutionId.trim() !== '') return;

    const singleOption = resolutionOptions[0];
    if (singleOption?.id) {
      setValue(`items.${itemId}.resolution.resolutionId`, singleOption.id, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isLoading, resolutionOptions, selectedResolutionId, setValue, itemId]);

  if (
    !itemId ||
    (!isLoading && (!resolutionOptions || resolutionOptions.length < 1))
  ) {
    return null;
  }

  const navigateToExchange = (resolutionId: string) => {
    navigate({
      to: '/$retailer/exchange',
      params: { retailer: retailer ?? 's' },
      search: (prev) => ({ ...prev, itemId, resolutionId }),
    });
  };

  const handleChange: ComponentProps<'input'>['onChange'] = (e) => {
    const value = e.target.value;
    const resolution = resolutionOptions?.find((o) => o.id === value);
    if (resolution?.exchanges) {
      navigateToExchange(value);
    }
  };

  const handleClick = (option: ResolutionOption) => {
    if (option.exchanges) {
      navigateToExchange(option.id);
    }
  };

  return (
    <ResolutionTaskView
      itemId={itemId}
      isLoading={isLoading}
      resolutionOptions={resolutionOptions}
      exchangeItem={exchangeItem}
      onChange={handleChange}
      onResolutionClick={handleClick}
      register={register}
    />
  );
};
